import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import { EstoqueContext } from 'contexts/EstoqueContext';
import { toastError } from 'utils/toast';
import localeText from 'utils/localeText';
import Container from 'components/Container';
import Header from 'components/Header';
import Button from 'components/Button';
import Card from 'components/Card';
import styles from './styles';

const Transferencia = () => {
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [itens, setItens] = useState([]);
  const [reader, setReader] = useState('');
  const [origem, setOrigem] = useState(null);
  const [destino, setDestino] = useState(null);
  const [etqOrigem, setEtqOrigem] = useState(null);
  const [etqDestino, setEtqDestino] = useState(null);
  const {
    getLoading,
    postLoading,
    getTransferenciaSaldo,
    postTransferenciaEndereco,
  } = useContext(EstoqueContext);
  const shouldReadDestino = Boolean(origem);
  const type = shouldReadDestino ? 'REPOSIÇÃO' : 'RETIRADA';
  const loading = getLoading || postLoading;

  const validateEndereco = ({ endereco, itens = [], etiqueta = null }) => {
    if (!endereco)
      return toastError({ message: 'Endereço de estoque não encontrado' });
    if (endereco?.ativo !== 'SIM')
      return toastError({ message: 'Endereço de estoque inativo' });

    if (shouldReadDestino) {
      setDestino(endereco);
      setEtqDestino(etiqueta);
    } else {
      setItens(itens);
      setOrigem(endereco);
      setEtqOrigem(etiqueta);
    }
  };

  const onReadEndereco = (etiqueta) => {
    if (!etiqueta || !Boolean(etiqueta?.ceendereco_id)) {
      return toastError({ message: 'Erro na leitura da etiqueta' });
    }

    const cb = (res = {}) => validateEndereco({ ...res, etiqueta });
    getTransferenciaSaldo({ params: { etiqueta }, cb });
  };

  const handleCancel = () => {
    setItens([]);
    setOrigem(null);
    setDestino(null);
    setEtqOrigem(null);
    setEtqDestino(null);
  };

  const handleBlur = () => inputRef.current.focus();

  const onChangeText = (e) => setReader(e?.target?.value);

  useEffect(() => {
    if (!reader) return;

    const timeout = setTimeout(() => {
      let etiqueta = null;
      try {
        etiqueta = JSON.parse(reader);
      } catch (error) {}
      onReadEndereco(etiqueta);
      setReader('');
    }, 1000);

    return () => clearTimeout(timeout);
  }, [reader]);

  useEffect(() => {
    if (Boolean(origem) && Boolean(destino)) {
      const data = { endereco_org: origem?.id, endereco_dest: destino?.id };
      postTransferenciaEndereco({ data, cb: handleCancel });
    }
  }, [origem, destino]);

  return (
    <Container>
      <Header titulo="Transferência" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Box display="flex" alignItems="center" gap={5}>
              <Box>
                <QrCode sx={styles?.qrcode} color="primary" />
                <Typography
                  flex={1}
                  color="primary"
                  textAlign="center"
                  variant="subtitle2"
                >
                  {shouldReadDestino ? 'DESTINO' : 'ORIGEM'}
                </Typography>
              </Box>
              <Box flex={1} gap={1} display="flex" flexDirection="column">
                <Typography variant="h6" flex={1}>
                  Você está fazendo a {type}
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Endereço de Origem:
                  </Typography>
                  <Typography variant="caption">
                    {origem?.endereco || '-'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="caption" fontWeight="bold">
                    Endereço de Destino:
                  </Typography>
                  <Typography variant="caption">
                    {destino?.endereco || '-'}
                  </Typography>
                </Box>
                {shouldReadDestino && (
                  <Box alignSelf="start" mt={2} display="flex" gap={2}>
                    <Button
                      loading={loading}
                      color="secondary"
                      variant="contained"
                      onClick={handleCancel}
                    >
                      Cancelar
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Typography variant="h6">Itens em transferência</Typography>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={itens}
            columns={[
              {
                field: 'referencia',
                headerName: 'Referência',
                width: 150,
                sortable: false,
              },
              {
                field: 'descricao',
                headerName: 'Produto',
                flex: 1,
                minWidth: 300,
                sortable: false,
              },
              {
                field: 'quantidade',
                headerName: 'Quantidade',
                type: 'number',
                width: 200,
                sortable: false,
                renderCell: ({ row, value }) => {
                  return (
                    <Typography>
                      {value} {row?.unidade_medida || ''}
                    </Typography>
                  );
                },
              },
            ]}
            hideFooter
            autoHeight
            density="compact"
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            getRowId={(row) => JSON.stringify(row)}
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            localeText={localeText}
            loading={loading}
            components={{ LoadingOverlay: LinearProgress }}
          />
        </Grid>
      </Grid>
      <input
        autoFocus
        ref={inputRef}
        value={reader}
        onChange={onChangeText}
        style={styles?.hideInput}
        onBlur={handleBlur}
      />
    </Container>
  );
};
export default Transferencia;
